import { useQueryClient } from '@tanstack/react-query';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';

import { useUnassignProfile } from '@/api/competency-services/profile-assignment';
import { useGetUserPermissions } from '@/api/directory';
import { PerformanceProfileQueryKey } from '@/constants/QueryKeys';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

type DeactivatedProfileBannerProps = {
    profile: CompetencyProfile;
    employeeId: string;
    /** Called after a successful unassign so the parent can close/reset the flow. */
    onDone: () => void;
};

/**
 * Shown inside ProfileSelector when the currently assigned profile is deactivated.
 * Blocks editing and offers two unblock paths (gated by permission):
 *   1. Navigate to the Profiles admin page to reactivate the profile.
 *   2. Unassign the profile from the employee directly.
 */
export const DeactivatedProfileBanner = ({
    profile,
    employeeId,
    onDone,
}: DeactivatedProfileBannerProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const language = useSelectedAppLanguage();
    const queryClient = useQueryClient();
    const { handleError } = useError();

    const { data: permissions } = useGetUserPermissions();
    const canManageCompetencies = permissions?.canManageCompetencies ?? false;
    const canAssignProfiles = permissions?.canAssignCompetencyProfiles ?? false;

    const { mutateAsync: unassignProfile, isPending } = useUnassignProfile();

    const profileName = getTextByLanguage(profile.name, language);

    const handleUnassign = async () => {
        try {
            await unassignProfile({ profileId: profile.id, employeeIds: [employeeId] });
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT],
            });
            showSuccessToast({
                message: t(
                    'competencies.competencyProfilesAdmin.selector.deactivatedBanner.unassignSuccess',
                ),
            });
            onDone();
        } catch (error) {
            handleError({ error });
        }
    };

    const hasActions = canManageCompetencies || canAssignProfiles;

    return (
        <div
            className={'rounded-xl border border-amber-300 bg-amber-50 p-4'}
            data-testid={'deactivated-profile-banner'}
        >
            <div className={'flex items-start gap-2'}>
                <span
                    aria-hidden={'true'}
                    className={'shrink-0'}
                >
                    ⚠️
                </span>

                <div className={'flex flex-col gap-2'}>
                    <p className={'text-sm font-semibold text-amber-800'}>
                        {t('competencies.competencyProfilesAdmin.selector.deactivatedBanner.title')}
                    </p>

                    <p className={'text-sm text-amber-700'}>
                        {t('competencies.competencyProfilesAdmin.selector.deactivatedBanner.body', {
                            name: profileName,
                        })}
                    </p>

                    {!hasActions ? (
                        <p className={'text-sm text-amber-700 italic'}>
                            {t(
                                'competencies.competencyProfilesAdmin.selector.deactivatedBanner.noPermission',
                            )}
                        </p>
                    ) : (
                        <div className={'flex flex-wrap items-center gap-3 pt-1'}>
                            {canManageCompetencies && (
                                <button
                                    type={'button'}
                                    onClick={() => navigate({ path: RouteName.Competencies })}
                                    className={
                                        'text-sm font-medium text-amber-800 underline hover:text-amber-900'
                                    }
                                    data-testid={'deactivated-banner-manage-profiles'}
                                >
                                    {t(
                                        'competencies.competencyProfilesAdmin.selector.deactivatedBanner.goToProfiles',
                                    )}
                                </button>
                            )}

                            {canAssignProfiles && (
                                <button
                                    type={'button'}
                                    disabled={isPending}
                                    onClick={() => void handleUnassign()}
                                    className={
                                        'rounded-lg border border-amber-400 px-3 py-1 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50'
                                    }
                                    data-testid={'deactivated-banner-unassign'}
                                >
                                    {t(
                                        'competencies.competencyProfilesAdmin.selector.deactivatedBanner.unassign',
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
